import { apiUrl } from './api_config'

const API_BASE_URL = apiUrl('/print-requests')
const BOOKS_BY_TYPE_URL = apiUrl('/books/type')
export const PRINT_REQUEST_PAGE_SIZE = 20

async function request(url, options = {}) {
  const response = await fetch(url, {
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
    ...options,
  })
  if (!response.ok) {
    let message = `Print request failed (${response.status})`
    try {
      const error = await response.json()
      message = error.message || error.error || message
    } catch {
      // Keep the HTTP status message when the server does not return JSON.
    }
    throw new Error(message)
  }
  return response.status === 204 ? null : response.json()
}

function findList(data, keys) {
  if (Array.isArray(data)) return data
  if (!data || typeof data !== 'object') return []
  for (const key of keys) {
    const list = findList(data[key], keys)
    if (list.length || Array.isArray(data[key])) return list
  }
  return []
}

const firstValue = (record, keys) =>
  keys
    .map((key) => record?.[key])
    .find((value) => value !== undefined && value !== null && value !== '')

const getNestedLabel = (value, keys) => {
  if (typeof value !== 'object' || value === null) return value
  return firstValue(value, keys) ?? firstValue(value.user, keys)
}

function normalizePrintRequest(request) {
  const source =
    request?.printRequest ||
    request?.print_request ||
    request?.request ||
    request?.data ||
    request
  const book = firstValue(source, [
    'book_title',
    'bookTitle',
    'book_name',
    'bookName',
    'book',
    'title',
    'name',
  ])
  const requester = firstValue(source, [
    'requested_by',
    'requestedBy',
    'requesting_user',
    'requester',
    'requestBy',
    'requester_name',
    'user_name',
    'user',
  ])

  return {
    ...source,
    book_title: getNestedLabel(book, [
      'book_title',
      'bookTitle',
      'book_name',
      'bookName',
      'title',
      'name',
      'description',
    ]),
    requested_by: getNestedLabel(requester, [
      'user_fullname',
      'full_name',
      'fullName',
      'fullname',
      'name',
      'email',
    ]),
  }
}

export async function getPrintRequests({
  cursor = null,
  search = '',
  signal,
} = {}) {
  const params = new URLSearchParams({ limit: String(PRINT_REQUEST_PAGE_SIZE) })
  if (cursor) params.set('cursor', cursor)
  if (search.trim()) params.set('search', search.trim())
  const data = await request(`${API_BASE_URL}/all?${params}`, { signal })
  return {
    requests: findList(data, [
      'requests',
      'printRequests',
      'results',
      'items',
      'data',
    ]).map(normalizePrintRequest),
    nextCursor: data?.nextCursor ?? data?.next_cursor ?? null,
  }
}

export async function getPrintBooks(bookType, signal) {
  if (!bookType) throw new Error('Book type is required.')
  const data = await request(
    `${BOOKS_BY_TYPE_URL}/${encodeURIComponent(bookType)}`,
    { signal },
  )
  return findList(data, [
    'books',
    'bookList',
    'booklist',
    'publishedBooks',
    'published_books',
    'results',
    'items',
    'rows',
    'data',
  ])
}

export async function createPrintRequest(payload) {
  const data = await request(`${API_BASE_URL}/add`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
  return data?.data ?? data
}

export async function updatePrintRequest(requestId, payload) {
  const data = await request(
    `${API_BASE_URL}/update/${encodeURIComponent(requestId)}`,
    { method: 'PUT', body: JSON.stringify(payload) },
  )
  return data?.data ?? data
}

export async function approvePrintRequest(requestId, payload = {}) {
  const data = await request(
    `${API_BASE_URL}/approve/${encodeURIComponent(requestId)}`,
    { method: 'PUT', body: JSON.stringify(payload) },
  )
  return data?.data ?? data
}

export async function unapprovePrintRequest(requestId) {
  const data = await request(
    `${API_BASE_URL}/${encodeURIComponent(requestId)}/unapprove`,
    { method: 'PUT' },
  )
  return data?.data ?? data
}

export async function deletePrintRequest(requestId) {
  return request(`${API_BASE_URL}/delete/${encodeURIComponent(requestId)}`, {
    method: 'DELETE',
  })
}

export async function changeStatusToPrinted(requestId) {
  const data = await request(
    `${API_BASE_URL}/statusPrinted/${encodeURIComponent(requestId)}`,
    { method: 'PUT' },
  )
  return data?.data ?? data
}
